import React from 'react';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ARIA_ROLES } from 'shared/constants';
import MyAccount from 'src/components/forms/my-account';
import Preferences from 'src/account/preferences';
import { useIsFullstackDispensary } from 'src/hooks/use-is-fullstack-dispensary';
import { BankAccount } from 'src/account/bank-account';
import { RouteParam } from 'src/types';
import { useDposLoyalty } from '../dpos-loyalty-context';
import { ConsumerUser, AccountTab } from '../account.types';
import { OrderList, TabbedOrderList } from './order-list';
import { LoyaltyPanel } from './loyalty-panel';
import { ReferralsPanel } from './referrals-panel';

export type ContentProps = {
  user: ConsumerUser;
  accountTab: RouteParam;
};

export function Content({ user, accountTab }: ContentProps): JSX.Element | null {
  const flags = useFlags();
  const isFullstackDispensary = useIsFullstackDispensary();
  const { shouldDisplayLoyaltySection, shouldDisplayReferralsSection } = useDposLoyalty();
  const isInstoreOrderHistoryEnabled: boolean = flags['ecomm.instore-order-history.rollout'] ?? false;
  const enableTabbedOrderList = isInstoreOrderHistoryEnabled && isFullstackDispensary;

  if (typeof accountTab !== 'string') {
    return null;
  }

  if (accountTab === AccountTab.bankAccount) {
    return (
      <Container
        role={ARIA_ROLES.TAB_PANEL}
        id='account-tab-bank-account'
        aria-labelledby='account-tab-area-bank-account'
      >
        <BankAccount />
      </Container>
    );
  }

  if (accountTab === AccountTab.account) {
    return (
      <Container role={ARIA_ROLES.TAB_PANEL} id='account-tab-my-account' aria-labelledby='account-tab-area-my-account'>
        <MyAccount user={user} />
      </Container>
    );
  }

  if (accountTab === AccountTab.preferences) {
    return (
      <Container
        role={ARIA_ROLES.TAB_PANEL}
        id='account-tab-preferences'
        data-testid='account-tab-panel-preferences'
        aria-labelledby='account-tab-area-preferences'
      >
        <Preferences user={user} />
      </Container>
    );
  }

  if (accountTab === AccountTab.orders) {
    return (
      <Container
        role={ARIA_ROLES.TAB_PANEL}
        id='account-tab-orders'
        data-testid='account-tab-panel-orders'
        aria-labelledby='account-tab-area-orders'
      >
        {enableTabbedOrderList ? <TabbedOrderList user={user} /> : <OrderList user={user} />}
      </Container>
    );
  }

  if (accountTab === AccountTab.loyalty && shouldDisplayLoyaltySection) {
    return (
      <Container
        role={ARIA_ROLES.TAB_PANEL}
        id='account-tab-loyalty'
        data-testid='account-tab-panel-loyalty'
        aria-labelledby='account-tab-area-loyalty'
      >
        <LoyaltyPanel />
      </Container>
    );
  }

  if (accountTab === AccountTab.referrals && shouldDisplayReferralsSection) {
    return (
      <Container
        role={ARIA_ROLES.TAB_PANEL}
        id='account-tab-referrals'
        data-testid='account-tab-panel-referrals'
        aria-labelledby='account-tab-area-referrals'
      >
        <ReferralsPanel />
      </Container>
    );
  }

  return null;
}

const Container = styled.div`
  width: 100%;
`;
